import { useState, useRef, useCallback, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { MODELS, LANGUAGES, DEFAULT_MODEL, modelInfo, SAMPLE_RATE } from '../utils/whisperModels';
import { decodeToChannels16k, mixDownChannels } from '../utils/decodeChannels16k';
import { encodeWavPCM16 } from '../utils/audioEncode';
import {
  fmtBytes, fmtClock, baseName, download, downloadBlob,
  buildTimestampedText, buildSpeakerText, buildWorkbook, exportSet, XLSX_MIME,
  MAX_FILE_BYTES, LONG_AUDIO_SECONDS, VERY_LONG_AUDIO_SECONDS,
  estimateSeconds, describeDuration,
} from '../utils/transcriptExports';
import {
  assignSpeakers, defaultSpeakerLabel, mergeChannelTranscripts, defaultChannelSpeakerLabel,
} from '../utils/speakerCluster';
import { probeDuration } from '../utils/probeDuration';
import { LLM_MODELS, DEFAULT_LLM, llmInfo, hasWebGpu } from '../utils/llmModels';
import createTranscribeWorker from '../workers/createTranscribeWorker';
import createAnalysisWorker from '../workers/createAnalysisWorker';

const ACCEPT = 'audio/*,video/*,.mp3,.wav,.m4a,.aac,.flac,.ogg,.opus,.webm,.mp4,.mov,.m4v';

// Channel-based speaker splitting only applies at exactly 2 channels — see
// the plan's reasoning: the real case (GTL/ICMV, 2-party recordings
// generally) is always exactly 2, and the "Swap L/R" / "Left/Right channel"
// UI is inherently 2-party-shaped. 1 or 3+ channels take the mixed-mono path.
const SPEAKER_SPLIT_CHANNELS = 2;

const isVideoFile = f => !!f && (f.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(f.name));

// ── small UI pieces ──────────────────────────────────────────────────────────

function Bar({ pct, color = '#6366f1' }) {
  return (
    <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`,
        background: color, borderRadius: 999, transition: 'width .2s ease',
      }} />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ flex: '1 1 200px', minWidth: 180 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const selectStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13, color: '#0f172a',
  border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', cursor: 'pointer',
};

const smallButton = {
  background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600,
};

// Distinct enough to tell apart at a glance, and legible on white.
const SPEAKER_COLORS = [
  { dot: '#4f46e5', text: '#4338ca' },
  { dot: '#0d9488', text: '#0f766e' },
  { dot: '#db2777', text: '#be185d' },
  { dot: '#ea580c', text: '#c2410c' },
  { dot: '#7c3aed', text: '#6d28d9' },
  { dot: '#0891b2', text: '#0e7490' },
];

const STATUS_STYLES = {
  queued: { label: 'Queued', color: '#64748b', bg: '#f1f5f9' },
  decoding: { label: 'Reading audio', color: '#1d4ed8', bg: '#dbeafe' },
  transcribing: { label: 'Transcribing', color: '#4338ca', bg: '#e0e7ff' },
  diarizing: { label: 'Finding speakers', color: '#7e22ce', bg: '#f3e8ff' },
  summarizing: { label: 'Summarising', color: '#6d28d9', bg: '#ede9fe' },
  done: { label: 'Done', color: '#15803d', bg: '#dcfce7' },
  error: { label: 'Failed', color: '#b91c1c', bg: '#fee2e2' },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: s.color, background: s.bg,
      padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Transcribe() {
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [modelId, setModelId] = useState(DEFAULT_MODEL);
  const [language, setLanguage] = useState('');
  const [task, setTask] = useState('transcribe');

  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState('idle');   // 'loading-model' | 'transcribing'
  const [decodeProg, setDecodeProg] = useState(null); // fallback-decoder progress only — normal decodes never touch this
  const [decodeFiles, setDecodeFiles] = useState({});
  const [currentId, setCurrentId] = useState(null);
  const [modelFiles, setModelFiles] = useState({});
  const [chunkProg, setChunkProg] = useState({ done: 0, total: 0 });
  const [channelProg, setChannelProg] = useState(null); // { index, total } during a 2-channel job
  const [liveText, setLiveText] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [playhead, setPlayhead] = useState(0);
  const [copied, setCopied] = useState('');
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState('');
  const [excelling, setExcelling] = useState(false);
  const [sizeError, setSizeError] = useState('');

  const [diarizeEnabled, setDiarizeEnabled] = useState(false);
  const [numSpeakers, setNumSpeakers] = useState('2');
  const [diarizeProg, setDiarizeProg] = useState(null);  // { stage, done, total }
  const [editingSpeakerFor, setEditingSpeakerFor] = useState(null);

  const [llmEnabled, setLlmEnabled] = useState(false);
  const [llmModelId, setLlmModelId] = useState(DEFAULT_LLM);
  const [llmJobId, setLlmJobId] = useState(null);        // job currently summarising
  const [llmPartial, setLlmPartial] = useState('');
  const [llmProg, setLlmProg] = useState(null);          // { done, total }
  const [llmFiles, setLlmFiles] = useState({});          // download progress
  const [llmError, setLlmError] = useState('');

  const jobsRef = useRef([]);
  const workerRef = useRef(null);
  const diarizeWorkerRef = useRef(null);
  const diarizeRef = useRef(false);
  const llmRef = useRef(false);
  const analysisRejectRef = useRef(null);
  const runningRef = useRef(false);
  const cancelRef = useRef(false);
  const rejectRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRef = useRef(null);
  const activeRowRef = useRef(null);
  const startedAtRef = useRef(0);
  const nextIdRef = useRef(1);

  jobsRef.current = jobs;
  diarizeRef.current = diarizeEnabled;
  llmRef.current = llmEnabled;

  const info = modelInfo(modelId);
  const selected = jobs.find(j => j.id === selectedId) || null;
  const doneJobs = jobs.filter(j => j.status === 'done');

  const updateJob = useCallback((id, patch) => {
    setJobs(prev => prev.map(j => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  // Elapsed-time ticker for the job currently running.
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setElapsed((Date.now() - startedAtRef.current) / 1000), 500);
    return () => clearInterval(t);
  }, [running]);

  // Release every object URL on unmount.
  useEffect(() => () => {
    workerRef.current?.terminate();
    diarizeWorkerRef.current?.terminate();
    for (const job of jobsRef.current) {
      if (job.mediaUrl) URL.revokeObjectURL(job.mediaUrl);
    }
  }, []);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    // Checked up front, outside the state updater — a setState inside another
    // setState's updater runs during render and can be dropped or doubled.
    const tooBig = incoming.filter(f => f.size > MAX_FILE_BYTES);
    setSizeError(tooBig.length
      ? `${tooBig.map(f => f.name).join(', ')} — over ${fmtBytes(MAX_FILE_BYTES)}. `
        + 'A file that large cannot be read into browser memory at all. Split it, or '
        + 'extract just the audio track, and try again.'
      : '');

    // De-dupe against the live list rather than inside the updater, so a
    // double-invoked updater can't create duplicates.
    const sig = f => `${f.name}|${f.size}`;
    const have = new Set(jobsRef.current.map(j => sig(j.file)));
    const added = incoming
      .filter(f => f.size <= MAX_FILE_BYTES && !have.has(sig(f)))
      .map(f => ({
        id: nextIdRef.current++,
        file: f,
        name: f.name,
        size: f.size,
        isVideo: isVideoFile(f),
        mediaUrl: URL.createObjectURL(f),
        status: 'queued',
        duration: null,
        sourceRate: null,
        sourceFormat: null,
        channelCount: null,
        result: null,
        diarization: null,
        llmSummary: null,
        error: '',
        elapsed: 0,
      }));

    if (!added.length) return;

    setJobs(prev => [...prev, ...added]);
    setSelectedId(cur => cur ?? added[0].id);

    // Read each duration from metadata straight away. Without this the queue
    // estimate could only ever appear for files that had already been decoded
    // — by which point they are no longer queued, so it never showed at all.
    for (const job of added) {
      probeDuration(job.mediaUrl).then((duration) => {
        if (duration) updateJob(job.id, { duration });
      });
    }
  }, [updateJob]);

  const removeJob = useCallback((id) => {
    setJobs(prev => {
      const job = prev.find(j => j.id === id);
      if (job?.mediaUrl) URL.revokeObjectURL(job.mediaUrl);
      return prev.filter(j => j.id !== id);
    });
    setSelectedId(cur => (cur === id ? null : cur));
  }, []);

  const clearAll = useCallback(() => {
    for (const job of jobsRef.current) {
      if (job.mediaUrl) URL.revokeObjectURL(job.mediaUrl);
    }
    setJobs([]);
    setSelectedId(null);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // Route one job's worker traffic into component state, resolving when it ends.
  const transcribeOne = useCallback((worker, audio, jobId) => (
    new Promise((resolve, reject) => {
      rejectRef.current = reject;

      const onMessage = (e) => {
        const msg = e.data;
        if (msg.id !== jobId) return;      // stale job — ignore

        switch (msg.type) {
          case 'status':
            setStage(msg.stage);
            break;
          case 'model-progress':
            setModelFiles(prev => ({ ...prev, [msg.file]: { loaded: msg.loaded, total: msg.total } }));
            break;
          case 'model-file-done':
            setModelFiles(prev => (prev[msg.file]
              ? { ...prev, [msg.file]: { loaded: prev[msg.file].total, total: prev[msg.file].total } }
              : prev));
            break;
          case 'chunk-progress':
            setChunkProg({ done: msg.done, total: msg.total });
            setLiveText(msg.committed);
            break;
          case 'partial':
            setLiveText(`${msg.committed}${msg.partial}`);
            break;
          case 'done':
            worker.removeEventListener('message', onMessage);
            rejectRef.current = null;
            resolve(msg.result);
            break;
          case 'error':
            worker.removeEventListener('message', onMessage);
            rejectRef.current = null;
            reject(new Error(msg.message));
            break;
          default:
            break;
        }
      };

      worker.addEventListener('message', onMessage);

      // Copy the samples so the transfer doesn't neuter our own array.
      const buf = audio.slice().buffer;
      worker.postMessage({
        id: jobId,
        audio: buf,
        modelId,
        language: info.multilingual ? language : '',
        task: info.multilingual ? task : 'transcribe',
      }, [buf]);
    })
  ), [modelId, language, task, info.multilingual]);

  // Main-thread fallback for when a worker can't be constructed. No job id
  // routing needed here — it's a direct call, not a message channel.
  const transcribeOnMainThread = useCallback(async (audio) => {
    const { transcribe } = await import('../utils/whisper');
    return transcribe(
      {
        audio,
        modelId,
        language: info.multilingual ? language : '',
        task: info.multilingual ? task : 'transcribe',
      },
      (msg) => {
        if (msg.type === 'status') setStage(msg.stage);
        else if (msg.type === 'model-progress') {
          setModelFiles(prev => ({ ...prev, [msg.file]: { loaded: msg.loaded, total: msg.total } }));
        } else if (msg.type === 'chunk-progress') {
          setChunkProg({ done: msg.done, total: msg.total });
          setLiveText(msg.committed);
        } else if (msg.type === 'partial') {
          setLiveText(`${msg.committed}${msg.partial}`);
        }
      },
    );
  }, [modelId, language, task, info.multilingual]);

  // Diarization runs in its own worker: it needs transformers.js v4 for the
  // pyannote/WeSpeaker model classes, while transcription stays on v2, so
  // keeping them apart keeps each runtime in its own lazily-loaded chunk.
  const runDiarization = useCallback((audio, jobId) => (
    new Promise((resolve, reject) => {
      let worker = diarizeWorkerRef.current;
      if (!worker) {
        worker = createAnalysisWorker();
        worker.onerror = () => {};
        diarizeWorkerRef.current = worker;
      }

      const onMessage = (e) => {
        const msg = e.data;
        if (msg.id !== jobId) return;

        switch (msg.type) {
          case 'status':
            setStage(msg.stage);
            break;
          case 'model-progress':
            setModelFiles(prev => ({ ...prev, [msg.file]: { loaded: msg.loaded, total: msg.total } }));
            break;
          case 'diarize-progress':
            setDiarizeProg({ stage: msg.stage, done: msg.done, total: msg.total });
            break;
          case 'done':
            worker.removeEventListener('message', onMessage);
            setDiarizeProg(null);
            resolve(msg.result);
            break;
          case 'error':
            worker.removeEventListener('message', onMessage);
            setDiarizeProg(null);
            reject(new Error(msg.message));
            break;
          default:
            break;
        }
      };

      analysisRejectRef.current = reject;
      worker.addEventListener('message', onMessage);
      setModelFiles({});

      const buf = audio.slice().buffer;
      worker.postMessage({
        kind: 'diarize',
        id: jobId,
        audio: buf,
        numSpeakers: numSpeakers === 'auto' ? null : Number(numSpeakers),
      }, [buf]);
    })
  ), [numSpeakers]);

  // Used both by the batch (when "Write an AI summary" is on) and by the
  // per-transcript Generate button. The model is a 350 MB+ download and slow,
  // so the batch checkbox is off by default.
  const runLlmSummary = useCallback(async (job) => {
    if (llmJobId || !job?.result) return;

    setLlmJobId(job.id);
    setLlmPartial('');
    setLlmProg(null);
    setLlmFiles({});
    setLlmError('');

    // A speaker-attributed transcript gives the model far more to work with.
    const text = buildSpeakerText(job) || job.result.text;

    try {
      let worker = diarizeWorkerRef.current;
      if (!worker) {
        worker = createAnalysisWorker();
        worker.onerror = () => {};
        diarizeWorkerRef.current = worker;
      }

      const summary = await new Promise((resolve, reject) => {
        const onMessage = (e) => {
          const msg = e.data;
          if (msg.id !== `llm:${job.id}`) return;

          switch (msg.type) {
            case 'status':
              setStage(msg.stage);
              break;
            case 'model-progress':
              setLlmFiles(prev => ({ ...prev, [msg.file]: { loaded: msg.loaded, total: msg.total } }));
              break;
            case 'llm-progress':
              setLlmProg({ done: msg.done, total: msg.total });
              break;
            case 'llm-partial':
              setLlmPartial(msg.text);
              break;
            case 'done':
              worker.removeEventListener('message', onMessage);
              resolve(msg.result);
              break;
            case 'error':
              worker.removeEventListener('message', onMessage);
              reject(new Error(msg.message));
              break;
            default:
              break;
          }
        };

        analysisRejectRef.current = reject;
        worker.addEventListener('message', onMessage);
        worker.postMessage({
          kind: 'summarize',
          id: `llm:${job.id}`,
          text,
          modelId: llmModelId,
        });
      });

      updateJob(job.id, {
        llmSummary: {
          text: summary.summary,
          model: llmInfo(llmModelId).label,
          device: summary.device,
          passes: summary.passes,
        },
      });
    } catch (err) {
      setLlmError(err?.message || String(err));
    } finally {
      setLlmJobId(null);
      setLlmPartial('');
      setLlmProg(null);
    }
  }, [llmJobId, llmModelId, updateJob]);

  const runQueue = useCallback(async () => {
    if (runningRef.current) return;
    if (!jobsRef.current.some(j => j.status === 'queued')) return;

    runningRef.current = true;
    cancelRef.current = false;
    setRunning(true);
    trackToolAction('/transcribe', 'Audio & Video Transcription');

    // One worker for the whole batch, so the model loads once rather than once
    // per file. This is the main reason batching is faster than repeat runs.
    let worker = workerRef.current;
    if (!worker) {
      try {
        worker = createTranscribeWorker();
        worker.onerror = () => {};
        workerRef.current = worker;
      } catch {
        worker = null;         // fall back to the main thread below
      }
    }

    // Tracked separately from job status: a state update from updateJob() isn't
    // guaranteed to have flushed into jobsRef.current by the time the loop next
    // checks — a fast-resolving decode can beat React's re-render. Without
    // this, the loop can re-select the same "queued" job forever, each pass
    // creating a fresh worker listener and never releasing the last one.
    const claimed = new Set();

    while (!cancelRef.current) {
      const next = jobsRef.current.find(j => j.status === 'queued' && !claimed.has(j.id));
      if (!next) break;
      claimed.add(next.id);

      setCurrentId(next.id);
      setSelectedId(next.id);
      setModelFiles({});
      setChunkProg({ done: 0, total: 0 });
      setLiveText('');
      setElapsed(0);
      startedAtRef.current = Date.now();

      try {
        updateJob(next.id, { status: 'decoding', error: '' });
        setStage('decoding');
        setDecodeProg(null);
        setDecodeFiles({});

        // onEvent only ever fires for the rare fallback-decoder path (native
        // decoding failing first) — a normal file decodes silently and fast.
        const { channels, duration, sourceRate, sourceFormat } = await decodeToChannels16k(next.file, (msg) => {
          if (msg.type === 'status') setDecodeProg({ stage: msg.stage, fraction: 0 });
          else if (msg.type === 'model-progress') {
            setDecodeFiles(prev => ({ ...prev, [msg.file]: { loaded: msg.loaded, total: msg.total } }));
          } else if (msg.type === 'ffmpeg-progress') {
            setDecodeProg({ stage: 'ffmpeg-converting', fraction: msg.fraction });
          }
        });
        if (cancelRef.current) break;
        setDecodeProg(null);

        // The player always plays the ORIGINAL file the user uploaded — fine
        // for formats the browser already knows, but a WAV container can wrap
        // a codec (G.729/"ICMV", µ-law, A-law, ADPCM...) no browser can play
        // natively, even though our own decoder just read it fine. Rebuild a
        // plain PCM WAV from the same already-decoded audio so the player
        // always has something every browser can actually play — no extra
        // file read needed, sourceFormat already says which case this is:
        // null (browser's own decodeAudioData played the original directly)
        // or 'PCM' (plain PCM WAV) both mean the original already plays;
        // anything else (a legacy codec name, G.729, the ffmpeg fallback,
        // MP3-in-WAV) means it doesn't. Skipped for video — we only decoded
        // the audio track, and re-encoding would drop the video entirely.
        if (!next.isVideo && sourceFormat !== null && sourceFormat !== 'PCM') {
          try {
            const wavBuffer = encodeWavPCM16(channels, SAMPLE_RATE);
            const playableUrl = URL.createObjectURL(new Blob([wavBuffer], { type: 'audio/wav' }));
            const oldUrl = next.mediaUrl;
            updateJob(next.id, { mediaUrl: playableUrl });
            URL.revokeObjectURL(oldUrl);
          } catch {
            // A playback rebuild failing must not cost the transcript — the
            // player just falls back to the (possibly unplayable) original.
          }
        }

        // Exactly 2 channels (e.g. a GTL/ICMV call, one party per side) gets a
        // free, deterministic speaker split — transcribe each channel on its
        // own and merge by timestamp, instead of guessing from mixed audio.
        const channelSplit = channels.length === SPEAKER_SPLIT_CHANNELS;

        updateJob(next.id, {
          status: 'transcribing', duration, sourceRate, sourceFormat, channelCount: channels.length,
        });
        setStage('loading-model');

        let result;
        let diarization = null;

        if (channelSplit) {
          // Sequential, never concurrent: whisper.js's cached pipeline and its
          // progress-callback closures aren't safe for interleaved calls in
          // one worker context.
          const resultsByChannel = [];
          for (let ch = 0; ch < channels.length; ch++) {
            setChannelProg({ index: ch, total: channels.length });
            setChunkProg({ done: 0, total: 0 });
            setLiveText('');
            const routeId = `${next.id}:${ch}`;
            const r = worker
              ? await transcribeOne(worker, channels[ch], routeId)
              : await transcribeOnMainThread(channels[ch]);
            if (cancelRef.current) break;
            resultsByChannel.push(r);
          }
          setChannelProg(null);
          if (cancelRef.current) break;

          const merged = mergeChannelTranscripts(resultsByChannel);
          result = { text: merged.text, chunks: merged.chunks };
          // Pre-seeding names (rather than leaving them empty) means the
          // existing rename inputs show the default Left/Right labels right
          // away — renameSpeaker itself needs no changes for this to work.
          diarization = {
            speakerOf: merged.speakerOf,
            speakerCount: SPEAKER_SPLIT_CHANNELS,
            names: { 0: defaultChannelSpeakerLabel(0), 1: defaultChannelSpeakerLabel(1) },
            method: 'channel',
          };
        } else {
          const audio = mixDownChannels(channels);
          result = worker
            ? await transcribeOne(worker, audio, next.id)
            : await transcribeOnMainThread(audio);

          if (cancelRef.current) break;

          // Diarization has to happen here, while the decoded samples are still
          // in scope — they're released below and re-decoding would be wasteful.
          if (diarizeRef.current) {
            try {
              updateJob(next.id, { status: 'diarizing' });
              setStage('diarizing');
              const { turns, speakerCount } = await runDiarization(audio, next.id);
              if (cancelRef.current) break;
              diarization = {
                speakerOf: assignSpeakers(result.chunks, turns),
                speakerCount,
                names: {},
                method: 'ml',
              };
            } catch (err) {
              // A diarization failure must not cost the user their transcript.
              diarization = {
                speakerOf: [], speakerCount: 0, names: {}, method: 'ml', error: err?.message || String(err),
              };
            }
          }
        }

        // Show the transcript before the optional summary pass, so a slow model
        // doesn't hold back the thing the user actually came for.
        updateJob(next.id, {
          status: llmRef.current ? 'summarizing' : 'done',
          result,
          diarization,
          elapsed: (Date.now() - startedAtRef.current) / 1000,
        });
        // `channels`/`audio` fall out of scope here. Holding decoded samples
        // for a whole batch would be ~230 MB per hour of recording (per
        // channel) and crash the tab.

        if (llmRef.current) {
          // A summary failure is reported on the job and never costs the
          // transcript; the queue keeps moving either way.
          await runLlmSummary({ id: next.id, result, diarization });
          if (cancelRef.current) break;
          updateJob(next.id, { status: 'done' });
        }
      } catch (err) {
        if (cancelRef.current) break;
        // One bad file must not stop the batch.
        updateJob(next.id, { status: 'error', error: err?.message || String(err) });
      }
    }

    runningRef.current = false;
    setRunning(false);
    setCurrentId(null);
    setStage('idle');
    setLiveText('');
  }, [updateJob, transcribeOne, transcribeOnMainThread, runDiarization, runLlmSummary]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    rejectRef.current?.(new Error('Cancelled'));
    rejectRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;
    analysisRejectRef.current?.(new Error('Cancelled'));
    analysisRejectRef.current = null;
    diarizeWorkerRef.current?.terminate();
    diarizeWorkerRef.current = null;
    setDiarizeProg(null);
    setChannelProg(null);
    setLlmJobId(null);
    setLlmPartial('');
    setLlmProg(null);

    runningRef.current = false;
    setRunning(false);
    setCurrentId(null);
    setStage('idle');
    setLiveText('');
    setJobs(prev => prev.map(j => (
      j.status === 'decoding' || j.status === 'transcribing' ? { ...j, status: 'queued' } : j
    )));
  }, []);

  const requeue = useCallback((id) => {
    updateJob(id, { status: 'queued', error: '', result: null, diarization: null, llmSummary: null });
  }, [updateJob]);

  const speakerName = useCallback((job, idx) => (
    idx == null ? 'Unlabelled' : (job?.diarization?.names?.[idx] ?? defaultSpeakerLabel(idx))
  ), []);

  const renameSpeaker = useCallback((jobId, idx, name) => {
    setJobs(prev => prev.map(j => (j.id === jobId && j.diarization
      ? { ...j, diarization: { ...j.diarization, names: { ...j.diarization.names, [idx]: name } } }
      : j)));
  }, []);

  // Channel 0 is always physically "Left" by construction, so swapping is
  // just exchanging whichever names are currently in slots 0/1 — works even
  // after the user has already renamed one of them.
  const swapChannelSpeakers = useCallback((jobId) => {
    setJobs(prev => prev.map((j) => {
      if (j.id !== jobId || j.diarization?.method !== 'channel') return j;
      const names = { ...j.diarization.names };
      [names[0], names[1]] = [names[1], names[0]];
      return { ...j, diarization: { ...j.diarization, names } };
    }));
  }, []);

  // Manual correction: the model gets crosstalk wrong often enough that this
  // is not an optional extra.
  const reassignChunk = useCallback((jobId, chunkIdx, speakerIdx) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.diarization) return j;
      const speakerOf = [...j.diarization.speakerOf];
      speakerOf[chunkIdx] = speakerIdx;
      return { ...j, diarization: { ...j.diarization, speakerOf } };
    }));
    setEditingSpeakerFor(null);
  }, []);

  const seekTo = (sec) => {
    const el = mediaRef.current;
    if (!el || sec == null) return;
    el.currentTime = sec;
    el.play().catch(() => {});
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1600);
    } catch {
      /* clipboard blocked — the download buttons still work */
    }
  };

  const combinedText = useCallback(() => doneJobs.map(j => (
    `${'='.repeat(60)}\n${j.name}\n${'='.repeat(60)}\n\n${j.result.text}\n`
  )).join('\n'), [doneJobs]);

  const exportCombined = () => download('transcripts-combined.txt', combinedText(), 'text/plain');

  const exportExcel = useCallback(async () => {
    if (!doneJobs.length) return;
    setExcelling(true);
    setZipError('');
    try {
      const buffer = await buildWorkbook(doneJobs, { whisperModel: info.label });
      downloadBlob('transcripts.xlsx', new Blob([buffer], { type: XLSX_MIME }));
    } catch (err) {
      setZipError(err?.message || 'Could not build the spreadsheet.');
    } finally {
      setExcelling(false);
    }
  }, [doneJobs, info.label]);

  // One folder per recording, plus a combined read-through at the root. Loaded
  // on demand so jszip stays out of the main bundle.
  const exportZip = useCallback(async () => {
    if (!doneJobs.length) return;
    setZipping(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const used = new Set();
      for (const job of doneJobs) {
        // Two recordings can share a name; keep them in separate folders.
        const base = baseName(job.name);
        let stem = base;
        let n = 2;
        while (used.has(stem)) stem = `${base}-${n++}`;
        used.add(stem);

        const dir = zip.folder(stem);
        for (const [filename, contents] of Object.entries(exportSet(job, stem))) {
          dir.file(filename, contents);
        }
      }

      if (doneJobs.length > 1) zip.file('all-transcripts.txt', combinedText());

      // Every version of every transcript, one row per recording.
      zip.file('all-transcripts.xlsx', await buildWorkbook(doneJobs, { whisperModel: info.label }));

      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob('transcripts.zip', blob);
    } catch (err) {
      setZipError(err?.message || 'Could not build the ZIP file.');
    } finally {
      setZipping(false);
    }
  }, [doneJobs, combinedText, info.label]);

  // Aggregate download percentage across the model's shards.
  const modelEntries = Object.values(modelFiles);
  const modelLoaded = modelEntries.reduce((a, f) => a + (f.loaded || 0), 0);
  const modelTotal = modelEntries.reduce((a, f) => a + (f.total || 0), 0);
  const modelPct = modelTotal ? (modelLoaded / modelTotal) * 100 : 0;

  const decodeEntries = Object.values(decodeFiles);
  const decodeLoaded = decodeEntries.reduce((a, f) => a + (f.loaded || 0), 0);
  const decodeTotal = decodeEntries.reduce((a, f) => a + (f.total || 0), 0);
  const decodePct = decodeTotal ? (decodeLoaded / decodeTotal) * 100 : (decodeProg?.fraction || 0) * 100;

  const llmEntries = Object.values(llmFiles);
  const llmLoaded = llmEntries.reduce((a, f) => a + (f.loaded || 0), 0);
  const llmTotal = llmEntries.reduce((a, f) => a + (f.total || 0), 0);
  const llmPct = llmTotal ? (llmLoaded / llmTotal) * 100 : 0;

  const chunkPct = chunkProg.total ? (chunkProg.done / chunkProg.total) * 100 : 0;
  const currentJob = jobs.find(j => j.id === currentId);
  const speed = currentJob?.duration && elapsed > 2 && chunkPct > 0
    ? (currentJob.duration * (chunkPct / 100)) / elapsed
    : null;

  const queuedCount = jobs.filter(j => j.status === 'queued').length;

  // Only files that have already been decoded once have a known duration, so
  // this fills in as the batch progresses rather than being right up front.
  const queuedKnownDuration = jobs
    .filter(j => j.status === 'queued' && j.duration)
    .reduce((a, j) => a + j.duration, 0);
  const estimatedSeconds = estimateSeconds(queuedKnownDuration, {
    speed: info.speed ?? 3,
    diarize: diarizeEnabled,
    summarize: llmEnabled,
  });

  const longJobs = jobs.filter(j => j.duration >= LONG_AUDIO_SECONDS);
  const narrowband = selected?.sourceRate && selected.sourceRate <= 8000;

  // Which transcript row the player is currently in, so it can be scrolled
  // into view automatically as playback moves — computed once here (not
  // per-row) so the scroll effect below has a single, stable value to key off.
  const activeChunkIndex = selected?.result?.chunks?.findIndex(
    c => playhead >= (c.start ?? 0) && playhead < (c.end ?? Infinity)
  ) ?? -1;

  // Follow playback down the transcript. `block: 'nearest'` only moves the
  // scroll position when the active row is actually out of view, rather than
  // re-centering on every tick — so it doesn't fight a user who's scrolled
  // up to reread an earlier line while audio keeps playing elsewhere.
  useEffect(() => {
    if (activeChunkIndex < 0) return;
    activeRowRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [activeChunkIndex]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>
      <Navbar pageTitle="Audio & Video Transcription" />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 72px' }}>

        {/* Privacy explainer — this is the whole point of the tool */}
        <div style={{
          background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 20, lineHeight: 1 }}>🔒</div>
          <div style={{ fontSize: 13, color: '#065f46', lineHeight: 1.6 }}>
            <strong>Your recordings never leave this computer.</strong> The speech model (OpenAI&apos;s
            Whisper) runs inside your browser tab — the audio is decoded and transcribed locally, and
            nothing is uploaded to any server.
            <div style={{ marginTop: 6, color: '#047857' }}>
              The one network request is a <strong>one-time download of the model itself</strong> from
              Hugging Face&apos;s CDN the first time you pick a size. Your browser caches it, so after
              that first load you can turn WiFi off and keep transcribing.
            </div>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#6366f1' : '#cbd5e1'}`,
            borderRadius: 12,
            padding: jobs.length ? '22px 24px' : '40px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? '#eef2ff' : '#fff',
            transition: 'border-color .15s, background .15s',
            marginBottom: 20,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <div style={{ fontSize: jobs.length ? 26 : 36, marginBottom: 8 }}>{jobs.length ? '🎧' : '🎙️'}</div>
          <div style={{ fontWeight: 700, fontSize: jobs.length ? 14 : 16, color: '#0f172a', marginBottom: 4 }}>
            {jobs.length ? 'Add more files' : 'Drop audio or video files here, or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            MP3, WAV (including µ-law, A-law and ADPCM), M4A, FLAC, OGG, and the audio track of
            MP4/MOV · several files at once · all processed locally
          </div>
        </div>

        {/* What this can actually handle, before you queue 40 jail calls. */}
        {!jobs.length && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '14px 18px', marginBottom: 20, fontSize: 12.5, color: '#475569', lineHeight: 1.65,
          }}>
            <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 13 }}>
              What this can handle
            </div>
            <div>
              <strong>How many files:</strong> as many as you like. They run one after another and
              each file&apos;s audio is released as soon as it finishes, so a long queue costs time
              rather than memory. You can add more while it works.
            </div>
            <div style={{ marginTop: 5 }}>
              <strong>How long each file can be:</strong> this is the real limit. Everything runs in
              this browser tab, and an hour of audio needs roughly 230&nbsp;MB of memory while it is
              being processed — double that for a 2-channel call recording, since each side is kept
              separate. Up to an hour is comfortable, one to three hours will be slow, and past
              three hours you risk exhausting the tab — split those first.
            </div>
            <div style={{ marginTop: 5 }}>
              <strong>How long it takes:</strong> roughly a third of the recording&apos;s length on
              the Base model, plus a one-time model download. Larger models, speaker identification
              and AI summaries each add to that.
            </div>
          </div>
        )}

        {sizeError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#b91c1c', lineHeight: 1.6,
          }}>
            {sizeError}
          </div>
        )}

        {/* Settings + run */}
        {jobs.length > 0 && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <Field label="Model" hint={`${info.size} download · ${info.note}`}>
                <select
                  value={modelId}
                  disabled={running}
                  onChange={(e) => setModelId(e.target.value)}
                  style={selectStyle}
                >
                  {MODELS.map(m => (
                    <option key={m.id} value={m.id}>{m.label} — {m.size}</option>
                  ))}
                </select>
              </Field>

              {info.multilingual && (
                <>
                  <Field label="Spoken language" hint="Auto-detect works, but naming it is more reliable.">
                    <select value={language} disabled={running} onChange={(e) => setLanguage(e.target.value)} style={selectStyle}>
                      {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Output" hint={task === 'translate' ? 'Translates speech into English.' : 'Keeps the original language.'}>
                    <select value={task} disabled={running} onChange={(e) => setTask(e.target.value)} style={selectStyle}>
                      <option value="transcribe">Transcribe as spoken</option>
                      <option value="translate">Translate to English</option>
                    </select>
                  </Field>
                </>
              )}
            </div>

            {/* Speaker identification — opt-in, because it costs a separate
                ~33 MB model download and a second pass over the audio. */}
            <div style={{
              marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9',
              display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start',
            }}>
              <label style={{
                display: 'flex', gap: 9, alignItems: 'flex-start',
                cursor: running ? 'default' : 'pointer', flex: '1 1 320px',
              }}>
                <input
                  type="checkbox"
                  checked={diarizeEnabled}
                  disabled={running}
                  onChange={(e) => setDiarizeEnabled(e.target.checked)}
                  style={{ marginTop: 2, cursor: running ? 'default' : 'pointer' }}
                />
                <span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    Identify speakers
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: '#94a3b8', marginTop: 2, lineHeight: 1.5 }}>
                    Labels each line with who was speaking. Downloads ~33 MB of extra models the
                    first time and roughly doubles processing time. Skipped automatically for any
                    file with 2 separate audio channels — those are split by channel instead,
                    which is more reliable than guessing.
                  </span>
                </span>
              </label>

              {diarizeEnabled && (
                <Field label="Expected speakers" hint="Naming the count is far more reliable than auto.">
                  <select
                    value={numSpeakers}
                    disabled={running}
                    onChange={(e) => setNumSpeakers(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="2">2 — interview or phone call</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="auto">Auto-detect</option>
                  </select>
                </Field>
              )}
            </div>

            {diarizeEnabled && (
              <div style={{
                marginTop: 12, fontSize: 12, color: '#92400e', background: '#fffbeb',
                border: '1px solid #fde68a', borderRadius: 8, padding: '9px 12px', lineHeight: 1.6,
              }}>
                <strong>Speaker labels are a guess, not evidence.</strong> This works reasonably on a
                clean two-person interview and noticeably worse on jail calls, crosstalk, and
                overlapping speech. Check every label against the audio before relying on it — you
                can correct any of them by clicking the name in the transcript. Files with 2
                separate audio channels skip this and use the channel itself instead, which isn&apos;t
                a guess.
              </div>
            )}

            {/* AI summary — also opt-in, and a much larger download again. */}
            <div style={{
              marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9',
              display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start',
            }}>
              <label style={{
                display: 'flex', gap: 9, alignItems: 'flex-start',
                cursor: running ? 'default' : 'pointer', flex: '1 1 320px',
              }}>
                <input
                  type="checkbox"
                  checked={llmEnabled}
                  disabled={running}
                  onChange={(e) => setLlmEnabled(e.target.checked)}
                  style={{ marginTop: 2, cursor: running ? 'default' : 'pointer' }}
                />
                <span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    Write an AI summary
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: '#94a3b8', marginTop: 2, lineHeight: 1.5 }}>
                    Summarises every file in the batch after transcribing it. Downloads{' '}
                    {llmInfo(llmModelId).size} of models the first time and adds real time per file —
                    a lot on a large batch.
                  </span>
                </span>
              </label>

              {llmEnabled && (
                <Field label="Summary model" hint={llmInfo(llmModelId).note}>
                  <select
                    value={llmModelId}
                    disabled={running}
                    onChange={(e) => setLlmModelId(e.target.value)}
                    style={selectStyle}
                  >
                    {LLM_MODELS.map(m => (
                      <option key={m.id} value={m.id}>{m.label} — {m.size}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            {llmEnabled && (
              <div style={{
                marginTop: 12, fontSize: 12, color: '#92400e', background: '#fffbeb',
                border: '1px solid #fde68a', borderRadius: 8, padding: '9px 12px', lineHeight: 1.6,
              }}>
                <strong>AI summaries can state things that were never said.</strong> Unlike the
                transcript, a summary is newly written text, not a quote — it can invent an
                admission, a name, or a date. Use it to find your way around a recording, never as a
                source. Anything you rely on must be read back in the transcript and heard in the
                audio.
                {!hasWebGpu() && (
                  <div style={{ marginTop: 6, color: '#b45309' }}>
                    This browser has no WebGPU, so summaries run on the CPU — expect several minutes
                    per file. Chrome or Edge will be far faster.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
              <button
                onClick={runQueue}
                disabled={running || !queuedCount}
                style={{
                  background: running || !queuedCount ? '#c7d2fe' : '#4f46e5',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 22px', fontSize: 14, fontWeight: 700,
                  cursor: running || !queuedCount ? 'default' : 'pointer',
                }}
              >
                {running
                  ? 'Working…'
                  : queuedCount > 1 ? `Transcribe ${queuedCount} files` : 'Start transcription'}
              </button>
              {running && (
                <button onClick={cancel} style={{ ...smallButton, padding: '9px 18px', fontSize: 13 }}>
                  Cancel
                </button>
              )}
              <span style={{ fontSize: 12, color: '#94a3b8' }}>
                {queuedKnownDuration > 0
                  ? `Roughly ${describeDuration(estimatedSeconds)} for the queue, on top of any model download.`
                  : 'The model loads once for the whole batch.'}
              </span>
            </div>
          </div>
        )}

        {/* File queue */}
        {jobs.length > 0 && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            marginBottom: 20, overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
              borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>
                {jobs.length} file{jobs.length !== 1 ? 's' : ''}
                <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>
                  {doneJobs.length} done{queuedCount ? ` · ${queuedCount} queued` : ''}
                </span>
              </div>
              {doneJobs.length > 0 && (
                <button onClick={exportZip} disabled={zipping} style={smallButton}>
                  {zipping ? 'Zipping…' : `Export all (.zip)`}
                </button>
              )}
              {doneJobs.length > 0 && (
                <button onClick={exportExcel} disabled={excelling} style={smallButton}>
                  {excelling ? 'Building…' : 'Excel (.xlsx)'}
                </button>
              )}
              {doneJobs.length > 1 && (
                <button onClick={exportCombined} style={smallButton}>Combined .txt</button>
              )}
              <button onClick={clearAll} disabled={running} style={{ ...smallButton, opacity: running ? 0.5 : 1 }}>
                Clear all
              </button>
            </div>

        {zipError && (
              <div style={{
                padding: '8px 18px', fontSize: 12, color: '#b91c1c',
                background: '#fef2f2', borderBottom: '1px solid #fecaca',
              }}>
                {zipError}
              </div>
            )}

            {jobs.map(job => (
              <div
                key={job.id}
                onClick={() => setSelectedId(job.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px',
                  borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  background: job.id === selectedId ? '#eef2ff' : 'transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {job.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {fmtBytes(job.size)}
                    {job.duration ? ` · ${fmtClock(job.duration)}` : ''}
                    {job.sourceFormat ? ` · ${job.sourceFormat}` : ''}
                    {job.channelCount === SPEAKER_SPLIT_CHANNELS ? ' · 2 channels (speakers split)' : ''}
                    {job.status === 'done' && job.result ? ` · ${job.result.chunks.length} segments` : ''}
                  </div>
                  {job.duration >= LONG_AUDIO_SECONDS && job.status !== 'error' && (
                    <div style={{
                      fontSize: 11.5,
                      color: job.duration >= VERY_LONG_AUDIO_SECONDS ? '#b91c1c' : '#b45309',
                      marginTop: 3, lineHeight: 1.5,
                    }}>
                      {job.duration >= VERY_LONG_AUDIO_SECONDS
                        ? `${fmtClock(job.duration)} long — this needs about ${fmtBytes(job.duration * 16000 * 4 * (job.channelCount || 1))} of memory and may exhaust the tab. Splitting it is safer.`
                        : `${fmtClock(job.duration)} long — expect this one to take a while.`}
                    </div>
                  )}
                  {job.error && (
                    <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4, lineHeight: 1.5 }}>
                      {job.error}
                    </div>
                  )}
                </div>

                <StatusChip status={job.status} />

                {job.status === 'error' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); requeue(job.id); }}
                    style={{ ...smallButton, padding: '3px 9px' }}
                  >
                    Retry
                  </button>
                )}
                {job.status === 'done' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      download(`${baseName(job.name)}-transcript.txt`, job.result.text, 'text/plain');
                    }}
                    title={`Download the transcript for ${job.name}`}
                    aria-label={`Download transcript for ${job.name}`}
                    style={{ ...smallButton, padding: '3px 9px' }}
                  >
                    .txt
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); removeJob(job.id); }}
                  disabled={running && job.id === currentId}
                  title={`Remove ${job.name}`}
                  aria-label={`Remove ${job.name}`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '0 4px',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Progress */}
        {running && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 8, gap: 12 }}>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentJob ? `${currentJob.name} — ` : ''}
                {channelProg ? `channel ${channelProg.index + 1} of ${channelProg.total} — ` : ''}
                {stage === 'decoding' && decodeProg?.stage === 'loading-fallback-decoder'
                  ? (decodeTotal ? `downloading fallback decoder, ${fmtBytes(decodeLoaded)} of ${fmtBytes(decodeTotal)}` : 'preparing fallback decoder…')
                  : stage === 'decoding' && decodeProg?.stage === 'ffmpeg-converting'
                    ? 'converting with fallback decoder…'
                    : stage === 'decoding'
                      ? 'reading audio…'
                      : stage === 'loading-llm'
                        ? (llmTotal ? `downloading summary model, ${fmtBytes(llmLoaded)} of ${fmtBytes(llmTotal)}` : 'preparing summary model…')
                        : stage === 'summarizing'
                          ? (llmProg && llmProg.total > 1
                            ? `summarising section ${Math.min(llmProg.done + 1, llmProg.total)} of ${llmProg.total}`
                            : 'writing summary…')
                          : stage === 'loading-model' || stage === 'loading-diarization'
                            ? (modelTotal ? `downloading model, ${fmtBytes(modelLoaded)} of ${fmtBytes(modelTotal)}` : 'preparing model…')
                            : diarizeProg
                              ? `${diarizeProg.stage === 'segmenting' ? 'finding speech' : 'comparing voices'} ${diarizeProg.done} of ${diarizeProg.total}`
                              : `window ${chunkProg.done} of ${chunkProg.total}`}
              </span>
              <span style={{ color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {fmtClock(elapsed)}{speed ? ` · ${speed.toFixed(1)}× real time` : ''}
              </span>
            </div>
            <Bar
              pct={
                stage === 'decoding' ? decodePct
                  : stage === 'loading-llm' ? llmPct
                    : stage === 'summarizing' ? (llmProg ? (llmProg.done / Math.max(1, llmProg.total)) * 100 : 0)
                      : stage === 'loading-model' || stage === 'loading-diarization' ? modelPct
                        : diarizeProg ? (diarizeProg.done / Math.max(1, diarizeProg.total)) * 100
                          : chunkPct
              }
              color={
                stage === 'decoding' ? '#0ea5e9'
                  : stage === 'loading-llm' || stage === 'summarizing' ? '#8b5cf6'
                    : diarizeProg || stage === 'diarizing' ? '#a855f7' : '#6366f1'
              }
            />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
              {stage === 'decoding' && decodeProg
                ? 'This file needs a fallback decoder not built into the browser — a one-time download, cached afterward.'
                : stage === 'loading-model'
                  ? 'First run only — the model is cached in your browser afterward.'
                  : `Running locally${queuedCount ? ` · ${queuedCount} file${queuedCount !== 1 ? 's' : ''} still queued` : ''}.`}
            </div>

            {(liveText || (stage === 'summarizing' && llmPartial)) && (
              <div style={{
                marginTop: 14, padding: '12px 14px', background: '#f8fafc',
                border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
                color: '#475569', lineHeight: 1.7, maxHeight: 160, overflowY: 'auto',
              }}>
                {stage === 'summarizing' ? llmPartial : liveText}
              </div>
            )}
          </div>
        )}

        {/* Player for the selected file */}
        {selected?.mediaUrl && (
          <div style={{ marginBottom: 20 }}>
            {selected.isVideo ? (
              <video
                ref={mediaRef}
                key={selected.id}
                src={selected.mediaUrl}
                controls
                onTimeUpdate={(e) => setPlayhead(e.target.currentTime)}
                style={{ width: '100%', maxHeight: 340, borderRadius: 10, background: '#000' }}
              />
            ) : (
              <audio
                ref={mediaRef}
                key={selected.id}
                src={selected.mediaUrl}
                controls
                onTimeUpdate={(e) => setPlayhead(e.target.currentTime)}
                style={{ width: '100%' }}
              />
            )}
            {narrowband && (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
                This is {(selected.sourceRate / 1000).toFixed(1)} kHz narrowband telephony audio —
                readable, but expect lower accuracy than a full-bandwidth recording.
              </div>
            )}
            {selected.diarization?.method === 'channel' && (
              <div style={{ fontSize: 12, color: '#0e7490', marginTop: 6 }}>
                This recording has 2 separate audio channels — Left and Right are being treated as
                separate speakers, which is typical of two-party call recordings (e.g. GTL/ICMV
                jail calls). This is read directly from the file, not a guess. If it&apos;s
                backward, use &quot;Swap L/R&quot; in the speaker list below.
              </div>
            )}
          </div>
        )}

        {/* AI summary — opt-in, per transcript */}
        {selected?.result && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            marginBottom: 20, overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
              padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 140 }}>
                AI summary
                {selected.llmSummary && (
                  <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>
                    {selected.llmSummary.model} · {selected.llmSummary.device === 'webgpu' ? 'WebGPU' : 'CPU'}
                  </span>
                )}
              </div>
              {selected.llmSummary && (
                <>
                  <button
                    onClick={() => copy(selected.llmSummary.text, 'llm')}
                    style={smallButton}
                  >
                    {copied === 'llm' ? 'Copied ✓' : 'Copy'}
                  </button>
                  <button
                    onClick={() => download(
                      `${baseName(selected.name)}-ai-summary.txt`,
                      `AI SUMMARY — ${selected.name}\n`
                      + `Generated locally by ${selected.llmSummary.model}. NOT a verbatim record —\n`
                      + `an AI wrote this and it can state things the recording never said.\n`
                      + `Always check it against the transcript.\n\n${selected.llmSummary.text}\n`,
                      'text/plain',
                    )}
                    style={smallButton}
                  >
                    .txt
                  </button>
                </>
              )}
            </div>

            <div style={{ padding: '14px 20px' }}>
              {/* The warning comes before the feature, not after it. */}
              <div style={{
                fontSize: 12.5, color: '#92400e', background: '#fffbeb',
                border: '1px solid #fde68a', borderRadius: 8, padding: '10px 13px',
                marginBottom: 14, lineHeight: 1.65,
              }}>
                <strong>Unlike the transcript, this is written by an AI and can be wrong.</strong> It
                produces new sentences rather than quoting, so it can state something that was never
                said — an admission, a name, a date. Treat it as a reading aid to find your way around
                a long recording, never as a source. Anything you intend to rely on must be read back
                in the transcript and heard in the audio.
              </div>

              {!selected.llmSummary && !llmJobId && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
                    <Field label="Summary model" hint={`${llmInfo(llmModelId).size} download · ${llmInfo(llmModelId).note}`}>
                      <select
                        value={llmModelId}
                        onChange={(e) => setLlmModelId(e.target.value)}
                        style={selectStyle}
                      >
                        {LLM_MODELS.map(m => (
                          <option key={m.id} value={m.id}>{m.label} — {m.size}</option>
                        ))}
                      </select>
                    </Field>
                    <button
                      onClick={() => runLlmSummary(selected)}
                      style={{
                        background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '10px 20px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      Generate summary
                    </button>
                  </div>

                  {!hasWebGpu() && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 10, lineHeight: 1.55 }}>
                      This browser has no WebGPU, so the model runs on the CPU — expect several
                      minutes for a long recording. Chrome or Edge will be far faster.
                    </div>
                  )}
                  {selected.diarization?.speakerCount > 0 && (
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
                      Speaker labels will be included, which gives the model more to work with.
                    </div>
                  )}
                </>
              )}

              {llmJobId === selected.id && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>
                    <span>
                      {llmProg
                        ? (llmProg.total > 1
                          ? `Summarising section ${Math.min(llmProg.done + 1, llmProg.total)} of ${llmProg.total}`
                          : 'Writing summary…')
                        : (llmTotal ? `Downloading model — ${fmtBytes(llmLoaded)} of ${fmtBytes(llmTotal)}` : 'Loading model…')}
                    </span>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>
                      {hasWebGpu() ? 'WebGPU' : 'CPU'}
                    </span>
                  </div>
                  <Bar
                    pct={llmProg ? (llmProg.done / Math.max(1, llmProg.total)) * 100 : llmPct}
                    color="#8b5cf6"
                  />
                  {llmPartial && (
                    <div style={{
                      marginTop: 12, padding: '12px 14px', background: '#f8fafc',
                      border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
                      color: '#475569', lineHeight: 1.7, maxHeight: 200, overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {llmPartial}
                    </div>
                  )}
                </div>
              )}

              {llmError && (
                <div style={{ fontSize: 12.5, color: '#b91c1c', lineHeight: 1.6 }}>
                  {llmError}
                </div>
              )}

              {selected.llmSummary && (
                <>
                  <div style={{ fontSize: 14, lineHeight: 1.75, whiteSpace: 'pre-wrap', color: '#0f172a' }}>
                    {selected.llmSummary.text}
                  </div>
                  {selected.llmSummary.passes > 1 && (
                    <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 10, lineHeight: 1.55 }}>
                      This recording was long enough to summarise in {selected.llmSummary.passes - 1} sections
                      and then combine them, so small errors can compound. Check it carefully.
                    </div>
                  )}
                  <button
                    onClick={() => { updateJob(selected.id, { llmSummary: null }); setLlmError(''); }}
                    style={{ ...smallButton, marginTop: 12 }}
                  >
                    Discard and regenerate
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Transcript for the selected file */}
        {selected?.result && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
              padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 160 }}>
                Transcript
                <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>
                  {selected.result.chunks.length} segment{selected.result.chunks.length !== 1 ? 's' : ''}
                  {selected.elapsed ? ` · ${fmtClock(selected.elapsed)}` : ''}
                </span>
              </div>
              {[
                { label: copied === 'text' ? 'Copied ✓' : 'Copy text', onClick: () => copy(selected.result.text, 'text') },
                { label: '.txt', onClick: () => download(`${baseName(selected.name)}-transcript.txt`, selected.result.text, 'text/plain') },
                { label: 'Timestamped .txt', onClick: () => download(`${baseName(selected.name)}-timestamped.txt`, buildTimestampedText(selected.result.chunks), 'text/plain') },
                ...(selected.diarization?.speakerCount ? [{
                  label: 'By speaker .txt',
                  onClick: () => download(
                    `${baseName(selected.name)}-speakers.txt`,
                    buildSpeakerText(selected, { timestamps: true }),
                    'text/plain',
                  ),
                }] : []),
              ].map(b => (
                <button key={b.label} onClick={b.onClick} style={smallButton}>{b.label}</button>
              ))}
            </div>

            {/* Speaker legend — rename here and it applies to every line */}
            {selected.diarization?.speakerCount > 0 && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
                padding: '11px 20px', borderBottom: '1px solid #f1f5f9', background: '#fdfdff',
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Speakers
                </span>
                {Array.from({ length: selected.diarization.speakerCount }, (_, idx) => (
                  <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      width: 10, height: 10, borderRadius: 999, flexShrink: 0,
                      background: SPEAKER_COLORS[idx % SPEAKER_COLORS.length].dot,
                    }} />
                    <input
                      value={speakerName(selected, idx)}
                      onChange={(e) => renameSpeaker(selected.id, idx, e.target.value)}
                      style={{
                        fontSize: 12.5, fontWeight: 600, padding: '3px 7px', width: 130,
                        border: '1px solid #e2e8f0', borderRadius: 6,
                        color: SPEAKER_COLORS[idx % SPEAKER_COLORS.length].text,
                      }}
                    />
                  </span>
                ))}
                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                  Rename to match the people on the recording.
                </span>
                {selected.diarization?.method === 'channel' && (
                  <button
                    onClick={() => swapChannelSpeakers(selected.id)}
                    style={{ ...smallButton, padding: '3px 9px', fontSize: 11.5 }}
                  >
                    Swap L/R
                  </button>
                )}
              </div>
            )}

            {selected.diarization?.error && (
              <div style={{
                padding: '9px 20px', fontSize: 12, color: '#b45309',
                background: '#fffbeb', borderBottom: '1px solid #fde68a',
              }}>
                Speaker identification failed ({selected.diarization.error}) — the transcript itself
                is unaffected.
              </div>
            )}

            {/* Timestamped segments — click to jump the player there */}
            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
              {selected.result.chunks.map((c, i) => {
                const active = i === activeChunkIndex;
                const speaker = selected.diarization?.speakerOf?.[i];
                const hasSpeaker = selected.diarization?.speakerCount > 0;
                const colors = speaker == null
                  ? { dot: '#cbd5e1', text: '#94a3b8' }
                  : SPEAKER_COLORS[speaker % SPEAKER_COLORS.length];
                const editing = editingSpeakerFor === `${selected.id}:${i}`;

                return (
                  <div
                    key={i}
                    ref={active ? activeRowRef : null}
                    onClick={() => seekTo(c.start)}
                    style={{
                      display: 'flex', gap: 14, padding: '10px 20px',
                      borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      background: active ? '#eef2ff' : 'transparent',
                    }}
                  >
                    <span style={{
                      fontFamily: 'monospace', fontSize: 12, color: '#6366f1',
                      fontWeight: 600, flexShrink: 0, paddingTop: 2, width: 58,
                    }}>
                      {fmtClock(c.start)}
                    </span>

                    {hasSpeaker && (
                      <span style={{ flexShrink: 0, width: 128, paddingTop: 1 }}>
                        {editing ? (
                          <select
                            autoFocus
                            value={speaker ?? ''}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => setEditingSpeakerFor(null)}
                            onChange={(e) => reassignChunk(
                              selected.id, i, e.target.value === '' ? null : Number(e.target.value),
                            )}
                            style={{
                              fontSize: 12, padding: '2px 4px', width: '100%',
                              border: '1px solid #cbd5e1', borderRadius: 5,
                            }}
                          >
                            <option value="">Unlabelled</option>
                            {Array.from({ length: selected.diarization.speakerCount }, (_, idx) => (
                              <option key={idx} value={idx}>{speakerName(selected, idx)}</option>
                            ))}
                          </select>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingSpeakerFor(`${selected.id}:${i}`); }}
                            title="Click to correct this speaker"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none',
                              border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                              fontSize: 12.5, fontWeight: 700, color: colors.text, maxWidth: '100%',
                            }}
                          >
                            <span style={{
                              width: 8, height: 8, borderRadius: 999, flexShrink: 0, background: colors.dot,
                            }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {speakerName(selected, speaker)}
                            </span>
                          </button>
                        )}
                      </span>
                    )}

                    <span style={{ fontSize: 14, lineHeight: 1.6, color: '#0f172a' }}>{c.text}</span>
                  </div>
                );
              })}
            </div>

            <div style={{
              padding: '12px 20px', background: '#fffbeb', borderTop: '1px solid #fde68a',
              fontSize: 12, color: '#92400e', lineHeight: 1.6,
            }}>
              Machine transcription is an investigative aid, not a certified record. Verify against the
              recording before quoting it in a report, affidavit, or testimony — Whisper can mishear
              names, numbers, and crosstalk, and occasionally invents text during silence.
              {selected.diarization?.method === 'channel' && (
                <> A channel where one party barely talks is transcribed on its own, mostly over
                silence — exactly the condition that triggers invented text, so check that side
                closely.</>
              )}
            </div>
          </div>
        )}

        {!jobs.length && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginTop: 40, lineHeight: 1.7 }}>
            Interviews, jail calls, body-cam audio, voicemails, and 911 recordings.<br />
            Drop in one file or a whole batch to get timestamped transcripts, key points, and
            extracted names, numbers and addresses.
          </div>
        )}
      </div>
    </div>
  );
}
